/**
 * Service Acts list (2026-06-03): Акти виконаних робіт/послуг.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { serviceActs, type ServiceActListItem } from '@/api/serviceActs';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function ServiceActsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['service-acts'],
    queryFn: () => serviceActs.list({ limit: 200 }),
  });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);

  const cols = useMemo<ColumnDef<ServiceActListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'contractor_id', header: 'Замовник',
      sortValue: (r) => contractorName.get(r.contractor_id) ?? '',
      cell: (r) => contractorName.get(r.contractor_id) ?? '',
    },
    {
      key: 'total_amount', header: 'Сума', width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'state', header: 'Стан', width: 110,
      sortValue: (r) => r.state ?? '',
      cell: (r) => {
        const v = r.state;
        const label = v === 'POSTED' ? '✓ Проведено' : v === 'CANCELLED' ? '✗ Скасовано' : 'Чернетка';
        const style = v === 'POSTED'
          ? { color: '#15803d', fontWeight: 600 }
          : { color: '#64748b', fontWeight: 400 };
        return <span style={style}>{label}</span>;
      },
    },
  ], [contractorName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Акти виконаних робіт/послуг</h2>
        <button className="primary" onClick={() => navigate('/service-acts/new')}>
          + Новий акт
        </button>
      </div>
      <DataTable<ServiceActListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/service-acts/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
